import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { 
  ArrowLeft, ShoppingCart, Trash2, Plus, Minus, CreditCard, 
  Lock, Package, Percent, AlertCircle, CheckCircle, Loader2,
  Wallet, Tag, Sparkles, Gift, Info
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';
import { getCart as getStoredCart, setCart as saveCart, clearCart as clearStoredCart } from '@/utils/cartStorage';
import { PartnerBadge } from '@/components/PartnerBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

// Initialize Stripe - will be loaded dynamically
let stripePromise = null;

const getStripePromise = async () => {
  if (!stripePromise) {
    try {
      const response = await axios.get(`${API_URL}/api/stripe/publishable-key`);
      stripePromise = loadStripe(response.data.publishable_key);
    } catch (error) {
      console.error('Failed to load Stripe key:', error);
      // No fallback - require the key from backend
      throw new Error('Could not load Stripe. Please try again.');
    }
  }
  return stripePromise;
};

// Payment Element appearance
const paymentElementAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#00F0FF',
    colorBackground: '#171717',
    colorText: '#ffffff',
    colorDanger: '#ef4444',
    fontFamily: 'system-ui, sans-serif',
    borderRadius: '12px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      backgroundColor: '#262626',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    '.Input:focus': {
      border: '1px solid #00F0FF',
      boxShadow: '0 0 0 1px #00F0FF',
    },
    '.Tab': {
      backgroundColor: '#262626',
      border: '1px solid rgba(255, 255, 255, 0.1)',
    },
    '.Tab:hover': {
      backgroundColor: '#333333',
    },
    '.Tab--selected': {
      backgroundColor: '#00F0FF',
      color: '#000000',
    },
    '.Label': {
      color: '#9ca3af',
    },
  },
};

// Payment Form Component
function PaymentForm({ customerInfo, cart, total, onSuccess, onBack, clientSecret, orderId, notifyOptIn, user, savePaymentMethod, setSavePaymentMethod }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentReady, setPaymentReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Confirm payment with PaymentElement (supports all payment methods)
      const { error: stripeError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/checkout/success?order_id=${orderId}`,
          payment_method_data: {
            billing_details: {
              name: customerInfo.name,
              email: customerInfo.email,
            },
          },
        },
        redirect: 'if_required',
      });

      if (stripeError) {
        setError(stripeError.message);
        toast.error(stripeError.message);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Payment succeeded without redirect (card payments)
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        // Confirm payment on backend
        await axios.post(
          `${API_URL}/api/confirm-payment`,
          null,
          { 
            params: { 
              order_id: orderId, 
              payment_intent_id: paymentIntent.id,
              notify_opt_in: notifyOptIn || false
            },
            headers 
          }
        );

        toast.success('Payment successful!');
        onSuccess(orderId);
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || 'Payment failed. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
        <CreditCard className="w-8 h-8 text-[#00F0FF]" />
        Payment
      </h1>

      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
        {/* Order Summary in Payment */}
        <div className="mb-8">
          <h3 className="font-semibold mb-4">Order Summary</h3>
          {cart.map((item) => (
            <div key={item.product_id} className="flex justify-between py-2 border-b border-white/5">
              <span className="text-neutral-400">
                {item.name} × {item.quantity || 1}
              </span>
              <span>${(item.price * (item.quantity || 1)).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Customer Info Display */}
        <div className="bg-neutral-800/50 rounded-xl p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-neutral-400">Billing to:</p>
              <p className="font-semibold">{customerInfo.name}</p>
              <p className="text-neutral-400">{customerInfo.email}</p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-[#00F0FF]"
            >
              Edit
            </Button>
          </div>
        </div>

        {/* Payment Input */}
        <div className="mb-6">
          <label className="block text-sm font-semibold mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4" />
            Payment Method
          </label>
          <div className="bg-neutral-800 border border-white/10 rounded-xl p-4">
            <PaymentElement 
              options={{
                layout: 'tabs',
                paymentMethodOrder: ['apple_pay', 'google_pay', 'card', 'cashapp', 'paypal'],
              }}
              onChange={(e) => setPaymentReady(e.complete)}
            />
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            Pay with card, Apple Pay, Google Pay, Cash App, or PayPal
          </p>
        </div>

        {/* Save Payment Method Checkbox - Only show for logged in users */}
        {user && (
          <div className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                checked={savePaymentMethod}
                onChange={(e) => setSavePaymentMethod(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 cursor-pointer"
              />
              <div>
                <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">
                  Save my payment method for future purchases
                </span>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Securely save your payment info for faster checkout next time
                </p>
              </div>
            </label>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </div>
          </div>
        )}

        {/* Security Badge */}
        <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-[#00F0FF] mb-1">Secure Payment</h4>
              <p className="text-sm text-neutral-400">
                Your payment is encrypted and processed securely by Stripe.
                You&apos;ll receive instant access to your products after purchase.
              </p>
            </div>
          </div>
        </div>

        {/* Test Mode Notice */}
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-500 mb-1">Test Mode</h4>
              <p className="text-sm text-neutral-400">
                Use card: <span className="font-mono text-yellow-400">4242 4242 4242 4242</span> 
                <br />Any future expiry, any 3-digit CVC.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-6">
        <Button
          type="submit"
          disabled={loading || !stripe || !paymentReady}
          className="w-full bg-white hover:bg-neutral-200 text-black rounded-full py-6 text-lg font-bold"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              <Lock className="w-5 h-5 mr-2" />
              Pay ${total.toFixed(2)}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}

function Checkout() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cart, setCart] = useState([]);
  const [user, setUser] = useState(null);
  const [step, setStep] = useState('cart'); // 'cart', 'info', 'payment'
  const [customerInfo, setCustomerInfo] = useState({
    email: '',
    name: '',
  });
  const [stripeLoaded, setStripeLoaded] = useState(false);
  const [stripeInstance, setStripeInstance] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [upsells, setUpsells] = useState([]);
  const [showFeeInfo, setShowFeeInfo] = useState(false);
  const [notifyOptIn, setNotifyOptIn] = useState(true);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [creators, setCreators] = useState([]);
  const [creatorPolicies, setCreatorPolicies] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe', 'paypal', or 'test'
  const [paypalOrderId, setPaypalOrderId] = useState(null);
  const [paypalLoading, setPaypalLoading] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(false); // Save payment for future use
  const [checkoutLoading, setCheckoutLoading] = useState(false); // For Stripe Checkout Sessions
  const [testCheckoutLoading, setTestCheckoutLoading] = useState(false);
  const [canUseTestMode, setCanUseTestMode] = useState(false);

  useEffect(() => {
    loadCart();
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      setCustomerInfo({
        email: parsedUser.email || '',
        name: parsedUser.name || '',
      });
      // Check if user can use test mode (VIP, Creator, or Admin)
      const isVip = parsedUser.vip_creator === true;
      const isCreator = parsedUser.role === 'creator';
      const isAdmin = parsedUser.role === 'admin';
      setCanUseTestMode(isVip || isCreator || isAdmin);
    }

    // Load Stripe
    getStripePromise().then(stripe => {
      setStripeInstance(stripe);
      setStripeLoaded(true);
    });

    // Listen for cart updates
    window.addEventListener('cartUpdated', loadCart);
    return () => window.removeEventListener('cartUpdated', loadCart);
  }, []);

  useEffect(() => {
    // Load upsells when cart changes
    if (cart.length > 0) {
      loadUpsells();
    }
  }, [cart]);

  const loadCart = () => {
    const storedCart = getStoredCart();
    setCart(storedCart);
    
    // Track cart for retargeting (abandoned cart emails)
    const token = localStorage.getItem('token');
    if (token && storedCart.length > 0) {
      axios.post(`${API_URL}/api/retargeting/track-cart`, 
        { items: storedCart.map(item => ({ product_id: item.product_id })) },
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch((err) => console.error('Cart tracking failed:', err)); // Log on fail
    }
  };

  const loadCreators = async () => {
    if (cart.length === 0) return;
    
    try {
      // Get unique creator info from cart items
      const creatorNames = new Set();
      const policies = [];
      
      for (const item of cart) {
        if (item.creator_name) {
          creatorNames.add(item.creator_name);
        }
        
        if (item.product_id) {
          // Fetch product to get creator info
          try {
            const { data } = await axios.get(`${API_URL}/api/products/${item.product_id}`);
            if (data.creator_name) {
              creatorNames.add(data.creator_name);
            }
            
            // Get creator's refund policy
            if (data.creator_id) {
              try {
                const creatorRes = await axios.get(`${API_URL}/api/creators/by-id/${data.creator_id}`);
                if (creatorRes.data && creatorRes.data.refund_policy) {
                  const existingPolicy = policies.find(p => p.store_name === creatorRes.data.store_name);
                  if (!existingPolicy) {
                    policies.push({
                      store_name: creatorRes.data.store_name,
                      refund_policy: creatorRes.data.refund_policy,
                      is_partner: !!creatorRes.data.is_partner,
                      account_type: creatorRes.data.account_type,
                    });
                  }
                }
              } catch (e) {
                // Creator lookup is per-item and best-effort; log for diagnostics.
                console.debug('Creator lookup failed, skipping:', e?.message || e);
              }
            }
          } catch (e) {
            // Product lookup is per-item and best-effort; log for diagnostics.
            console.debug('Product lookup failed, skipping:', e?.message || e);
          }
        }
      }
      setCreators(Array.from(creatorNames));
      setCreatorPolicies(policies);
    } catch (error) {
      console.error('Failed to load creators:', error);
    }
  };

  useEffect(() => {
    if (cart.length > 0) {
      loadCreators();
    }
  }, [cart]);

  const loadUpsells = async () => {
    try {
      const productIds = cart.map(item => item.product_id).join(',');
      const { data } = await axios.get(`${API_URL}/api/cart/upsells?product_ids=${productIds}`);
      setUpsells(data.upsells || []);
    } catch (error) {
      console.error('Failed to load upsells:', error);
    }
  };

  const addUpsellToCart = (upsell) => {
    const upsellProduct = upsell.upsell_product;
    
    // Inherit affiliate_ref from any existing cart item (for upsell commission)
    const existingAffiliateRef = cart.find(item => item.affiliate_ref)?.affiliate_ref || null;
    
    const newItem = {
      product_id: upsell.upsell_product_id,
      name: upsellProduct.name,
      price: upsellProduct.discounted_price, // Use discounted price
      original_price: upsellProduct.original_price,
      images: upsellProduct.images,
      quantity: 1,
      is_upsell: true,
      upsell_discount: upsell.discount_percent,
      affiliate_ref: existingAffiliateRef // Affiliate gets commission on upsells too
    };
    
    const updatedCart = [...cart, newItem];
    setCart(updatedCart);
    saveCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success(`Added ${upsellProduct.name} to cart!`);
    
    // Remove this upsell from display
    setUpsells(upsells.filter(u => u.upsell_id !== upsell.upsell_id));
  };

  const updateQuantity = (productId, delta) => {
    const updatedCart = cart.map(item => {
      if (item.product_id === productId) {
        const newQuantity = Math.max(1, (item.quantity || 1) + delta);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    setCart(updatedCart);
    saveCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
  };

  const removeItem = (productId) => {
    const updatedCart = cart.filter(item => item.product_id !== productId);
    setCart(updatedCart);
    saveCart(updatedCart);
    window.dispatchEvent(new Event('cartUpdated'));
    toast.success('Item removed from cart');
  };

  const clearCart = () => {
    setCart([]);
    clearStoredCart();
    window.dispatchEvent(new Event('cartUpdated'));
    
    // Mark retargeting as converted
    const token = localStorage.getItem('token');
    if (token) {
      axios.post(`${API_URL}/api/retargeting/mark-converted`, {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch((err) => console.error('Cart tracking failed:', err)); // Log on fail
    }
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0);
  const discount = couponApplied ? couponApplied.calculated_discount : 0;
  const platformFee = 0; // Free for buyers
  const total = subtotal - discount + platformFee;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    setCouponLoading(true);
    try {
      const productIds = cart.map(item => item.product_id);
      const params = new URLSearchParams({
        code: couponCode.toUpperCase(),
        subtotal: subtotal.toString()
      });
      productIds.forEach(id => params.append('product_ids', id));

      const { data } = await axios.post(`${API_URL}/api/coupons/validate?${params}`);
      
      setCouponApplied(data);
      toast.success(`Coupon applied! You save $${data.calculated_discount.toFixed(2)}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid coupon code';
      toast.error(message);
      setCouponApplied(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponApplied(null);
    toast.success('Coupon removed');
  };

  const handleProceedToInfo = () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }
    setStep('info');
  };

  const handleProceedToPayment = async () => {
    if (!customerInfo.email || !customerInfo.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Create payment intent before going to payment step
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const items = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity || 1,
        affiliate_ref: item.affiliate_ref || null
      }));

      const { data: paymentData } = await axios.post(
        `${API_URL}/api/create-payment-intent`,
        {
          items,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          save_payment_method: savePaymentMethod
        },
        { headers }
      );

      setClientSecret(paymentData.client_secret);
      setOrderId(paymentData.order_id);
      setStep('payment');
    } catch (error) {
      toast.error('Failed to initialize payment. Please try again.');
      console.error('Payment init error:', error);
    }
  };

  // Use Stripe Checkout Sessions for automatic tax calculation
  const handleStripeCheckoutWithTax = async () => {
    if (!customerInfo.email || !customerInfo.name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Debug: Log if token exists
      if (!token) {
        console.warn('No auth token found - checkout may fail');
      }
      
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const items = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity || 1,
        affiliate_ref: item.affiliate_ref || null
      }));

      const { data } = await axios.post(
        `${API_URL}/api/checkout-with-tax`,
        {
          items,
          customer_email: customerInfo.email,
          customer_name: customerInfo.name,
          origin_url: window.location.origin
        },
        { headers }
      );

      if (data.checkout_url) {
        // Redirect to Stripe Checkout (hosted page with automatic tax)
        window.location.href = data.checkout_url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Checkout failed: ${errorMessage}`);
      console.error('Stripe Checkout error:', error.response?.data || error);
      setCheckoutLoading(false);
    }
  };

  // Test checkout for VIPs/Creators during pre-launch
  const handleTestCheckout = async () => {
    if (cart.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setTestCheckoutLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please log in to use test checkout');
        setTestCheckoutLoading(false);
        return;
      }

      const items = cart.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity || 1
      }));

      const { data } = await axios.post(
        `${API_URL}/api/test-checkout`,
        { items },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (data.success) {
        toast.success('Test order completed! No payment was charged.');
        
        // Build orders array for success page
        const orders = data.items.map(item => ({
          order_id: data.order_id,
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity || 1,
          amount: item.price * (item.quantity || 1),
          creator_id: item.creator_id
        }));
        
        clearCart();
        navigate('/checkout/success', { 
          state: { 
            orders,
            isTestOrder: true,
            downloadLinks: data.download_links || []
          } 
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`Test checkout failed: ${errorMessage}`);
      console.error('Test checkout error:', error.response?.data || error);
    } finally {
      setTestCheckoutLoading(false);
    }
  };

  const handlePaymentSuccess = (orderId) => {
    // Build orders array from cart for success page
    const orders = cart.map(item => ({
      order_id: orderId,
      product_id: item.product_id,
      product_name: item.name,
      quantity: item.quantity || 1,
      amount: item.price * (item.quantity || 1),
      creator_id: item.creator_id,
      creator_name: item.creator_name || item.store_name
    }));
    
    // Get first creator info for affiliate prompt
    const firstCreator = cart[0];
    
    clearCart();
    navigate('/checkout/success', { 
      state: { 
        orders,
        customerInfo,
        total,
        creatorId: firstCreator?.creator_id,
        creatorName: firstCreator?.creator_name || firstCreator?.store_name
      } 
    });
  };

  // Empty cart view
  if (cart.length === 0 && step === 'cart') {
    return (
      <div className="min-h-screen bg-[#050505] text-white">
        <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
          <div className="container mx-auto px-6 md:px-12 py-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/')}
              className="text-[#00F0FF] hover:text-[#00C2CC]"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Continue Shopping
            </Button>
          </div>
        </header>
        
        <div className="container mx-auto px-6 md:px-12 py-20 text-center">
          <ShoppingCart className="w-24 h-24 text-neutral-600 mx-auto mb-6" />
          <h1 className="text-3xl font-bold mb-4">Your Cart is Empty</h1>
          <p className="text-neutral-400 mb-8">Looks like you haven&apos;t added any products yet.</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full px-8"
          >
            Browse Products
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => {
              if (step === 'cart') navigate('/');
              else if (step === 'info') setStep('cart');
              else setStep('info');
            }}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {step === 'cart' ? 'Continue Shopping' : step === 'info' ? 'Back to Cart' : 'Back to Info'}
          </Button>
          
          {/* Progress Steps */}
          <div className="hidden md:flex items-center gap-4">
            <div className={`flex items-center gap-2 ${step === 'cart' ? 'text-[#00F0FF]' : 'text-neutral-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'cart' ? 'bg-[#00F0FF] text-black' : step !== 'cart' ? 'bg-[#00F0FF]/30 text-[#00F0FF]' : 'bg-neutral-800'}`}>
                {step !== 'cart' ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="font-semibold">Cart</span>
            </div>
            <div className="w-12 h-px bg-neutral-700" />
            <div className={`flex items-center gap-2 ${step === 'info' ? 'text-[#00F0FF]' : 'text-neutral-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'info' ? 'bg-[#00F0FF] text-black' : step === 'payment' ? 'bg-[#00F0FF]/30 text-[#00F0FF]' : 'bg-neutral-800'}`}>
                {step === 'payment' ? <CheckCircle className="w-5 h-5" /> : '2'}
              </div>
              <span className="font-semibold">Info</span>
            </div>
            <div className="w-12 h-px bg-neutral-700" />
            <div className={`flex items-center gap-2 ${step === 'payment' ? 'text-[#00F0FF]' : 'text-neutral-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step === 'payment' ? 'bg-[#00F0FF] text-black' : 'bg-neutral-800'}`}>
                3
              </div>
              <span className="font-semibold">Payment</span>
            </div>
          </div>
          
          <div className="w-32" />
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Cart Step */}
            {step === 'cart' && (
              <div>
                <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
                  <ShoppingCart className="w-8 h-8 text-[#00F0FF]" />
                  Your Cart ({cart.length} {cart.length === 1 ? 'item' : 'items'})
                </h1>

                <div className="space-y-4">
                  {cart.map((item) => (
                    <div 
                      key={item.product_id}
                      className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 flex flex-col md:flex-row gap-6"
                    >
                      {/* Product Image */}
                      <div className="w-full md:w-32 h-32 rounded-xl overflow-hidden bg-neutral-800 flex-shrink-0">
                        <img 
                          src={item.images?.[0]?.startsWith('/api') 
                            ? `${API_URL}${item.images[0]}` 
                            : item.images?.[0]
                          }
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      {/* Product Info */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold mb-2">{item.name}</h3>
                        <p className="text-neutral-400 text-sm mb-4 line-clamp-2">{item.description}</p>
                        
                        {/* Affiliate badge if present */}
                        {item.affiliate_ref && (
                          <div className="flex items-center gap-2 mb-3">
                            <Percent className="w-4 h-4 text-[#00F0FF]" />
                            <span className="text-xs text-[#00F0FF]">Affiliate referral applied</span>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          {/* Quantity Controls */}
                          <div className="flex items-center gap-3">
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => updateQuantity(item.product_id, -1)}
                              className="w-8 h-8 border-white/10 hover:bg-white/5"
                            >
                              <Minus className="w-4 h-4" />
                            </Button>
                            <span className="w-8 text-center font-bold">{item.quantity || 1}</span>
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => updateQuantity(item.product_id, 1)}
                              className="w-8 h-8 border-white/10 hover:bg-white/5"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          {/* Price & Remove */}
                          <div className="flex items-center gap-6">
                            <span className="text-2xl font-bold text-[#00F0FF]">
                              ${(item.price * (item.quantity || 1)).toFixed(2)}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => removeItem(item.product_id)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-5 h-5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Info Step */}
            {step === 'info' && (
              <div>
                <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
                  <Package className="w-8 h-8 text-[#00F0FF]" />
                  Customer Information
                </h1>

                <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-8">
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Full Name *</label>
                      <Input
                        type="text"
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                        placeholder="John Doe"
                        className="bg-neutral-800 border-white/10"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Email Address *</label>
                      <Input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                        placeholder="john@example.com"
                        className="bg-neutral-800 border-white/10"
                        required
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        Your purchase confirmation and product access will be sent here.
                      </p>
                    </div>

                    {/* Terms Acceptance - moved here for Stripe Checkout flow */}
                    <div className="pt-4 border-t border-white/10">
                      <div className="flex items-start gap-3">
                        <input
                          type="checkbox"
                          id="info-terms-checkbox"
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="w-5 h-5 mt-0.5 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 flex-shrink-0 cursor-pointer"
                        />
                        <div className="text-sm text-neutral-300">
                          <label htmlFor="info-terms-checkbox" className="cursor-pointer hover:text-white transition-colors">
                            I have read and agree to the
                          </label>{' '}
                          <a 
                            href="/terms" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[#00F0FF] hover:underline"
                          >
                            Terms of Service
                          </a>,{' '}
                          <a 
                            href="/refund-policy" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[#00F0FF] hover:underline"
                          >
                            Refund Policy
                          </a>, and{' '}
                          <a 
                            href="/privacy" 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-[#00F0FF] hover:underline"
                          >
                            Privacy Policy
                          </a>.
                          <label htmlFor="info-terms-checkbox" className="cursor-pointer hover:text-white transition-colors">
                            {' '}I understand this is a digital product purchase.
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Tax Notice */}
                    <div className="bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <Info className="w-5 h-5 text-[#00F0FF] flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-neutral-300">
                            <span className="font-semibold text-[#00F0FF]">Sales Tax:</span> Applicable sales tax will be calculated automatically at checkout based on your billing address.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Step */}
            {step === 'payment' && stripeLoaded && stripeInstance && clientSecret && (
              <div className="space-y-6">
                {/* Terms of Sale & Disclaimers */}
                <div className="bg-neutral-900/80 border border-white/10 rounded-2xl p-6">
                  <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-400" />
                    Important Information
                  </h3>
                  
                  <div className="space-y-4 text-sm text-neutral-300">
                    {/* Digital Product Disclaimer */}
                    <div className="bg-neutral-800/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Digital Product Delivery</h4>
                      <p className="text-neutral-400">
                        This is a digital product. Upon successful payment, you will receive immediate access 
                        to download your purchased items. Delivery is handled electronically via your account 
                        dashboard and email.
                      </p>
                    </div>

                    {/* Refund Policy */}
                    <div className="bg-neutral-800/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Refund Policy</h4>
                      {creatorPolicies.length > 0 ? (
                        <div className="space-y-3">
                          {creatorPolicies.map((policy, idx) => (
                            <div key={`policy-${policy.store_name}-${idx}`} className="border-l-2 border-amber-500/50 pl-3">
                              <p className="text-xs text-amber-400 font-semibold mb-1 inline-flex items-center gap-2">
                                {policy.store_name}:
                                {policy.is_partner && <PartnerBadge size="xs" />}
                              </p>
                              <p className="text-neutral-400 text-sm">{policy.refund_policy}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-neutral-400">
                          Each seller sets their own refund policy. Please review the seller&apos;s refund policy 
                          on the product page before purchasing. Refund requests are handled directly by the seller.
                        </p>
                      )}
                    </div>

                    {/* Seller Responsibility */}
                    <div className="bg-neutral-800/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Third-Party Sellers</h4>
                      <p className="text-neutral-400">
                        Products are sold by independent creators. theailedger.ai acts as a marketplace 
                        facilitator. The seller is responsible for product quality, support, and any 
                        claims made about the product.
                      </p>
                    </div>
                  </div>

                  {/* Opt-in for Creator Updates */}
                  {creators.length > 0 && (
                    <div className="mt-6 pt-4 border-t border-white/10">
                      <label className="flex items-start gap-3 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={notifyOptIn}
                          onChange={(e) => setNotifyOptIn(e.target.checked)}
                          className="w-5 h-5 mt-0.5 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0"
                        />
                        <span className="text-sm text-neutral-300 group-hover:text-white transition-colors">
                          Notify me of updates and future products from{' '}
                          <span className="text-[#00F0FF] font-medium">
                            {creators.length === 1 
                              ? creators[0] 
                              : creators.slice(0, -1).join(', ') + ' and ' + creators[creators.length - 1]}
                          </span>
                        </span>
                      </label>
                    </div>
                  )}

                  {/* Terms Acceptance */}
                  <div className="mt-4 pt-4 border-t border-white/10">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="terms-checkbox"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        className="w-5 h-5 mt-0.5 rounded border-white/20 bg-neutral-800 text-[#00F0FF] focus:ring-[#00F0FF] focus:ring-offset-0 flex-shrink-0 cursor-pointer"
                        required
                      />
                      <div className="text-sm text-neutral-300">
                        <label htmlFor="terms-checkbox" className="cursor-pointer hover:text-white transition-colors">
                          I have read and agree to the
                        </label>{' '}
                        <a 
                          href="/terms" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[#00F0FF] hover:underline"
                        >
                          Terms of Service
                        </a>,{' '}
                        <a 
                          href="/refund-policy" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[#00F0FF] hover:underline"
                        >
                          Refund Policy
                        </a>, and{' '}
                        <a 
                          href="/privacy" 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-[#00F0FF] hover:underline"
                        >
                          Privacy Policy
                        </a>.
                        <label htmlFor="terms-checkbox" className="cursor-pointer hover:text-white transition-colors">
                          {' '}I understand this is a digital product purchase.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Form */}
                {acceptedTerms ? (
                  <div className="space-y-4">
                    {/* Payment Method Selection */}
                    <div className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
                      <h4 className="font-semibold mb-3">Choose Payment Method</h4>
                      <div className={`grid ${canUseTestMode ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                        <button
                          onClick={() => setPaymentMethod('stripe')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            paymentMethod === 'stripe' 
                              ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <CreditCard className="w-6 h-6" />
                          <span className="text-sm font-medium">Card / Wallet</span>
                          <span className="text-xs text-neutral-400">Visa, Mastercard, Apple Pay</span>
                        </button>
                        <button
                          onClick={() => setPaymentMethod('paypal')}
                          className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                            paymentMethod === 'paypal' 
                              ? 'border-[#0070ba] bg-[#0070ba]/10' 
                              : 'border-white/10 hover:border-white/30'
                          }`}
                        >
                          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z" fill="#0070ba"/>
                          </svg>
                          <span className="text-sm font-medium">PayPal</span>
                          <span className="text-xs text-neutral-400">Pay with PayPal</span>
                        </button>
                        {canUseTestMode && (
                          <button
                            onClick={() => setPaymentMethod('test')}
                            className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                              paymentMethod === 'test' 
                                ? 'border-[#00F0FF] bg-[#00F0FF]/10' 
                                : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <Sparkles className="w-6 h-6 text-[#00F0FF]" />
                            <span className="text-sm font-medium text-[#00F0FF]">Test Mode</span>
                            <span className="text-xs text-neutral-400">No charge (VIP)</span>
                          </button>
                        )}
                      </div>
                      {paymentMethod === 'test' && (
                        <div className="mt-3 p-3 bg-[#00F0FF]/10 border border-[#00F0FF]/30 rounded-lg">
                          <p className="text-sm text-[#00F0FF] flex items-center gap-2">
                            <Info className="w-4 h-4" />
                            Test Mode: No payment will be charged. Order will be completed instantly.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Stripe Payment Form */}
                    {paymentMethod === 'stripe' && clientSecret && (
                      <Elements 
                        stripe={stripeInstance}
                        options={{
                          clientSecret: clientSecret,
                          appearance: paymentElementAppearance,
                        }}
                      >
                        <PaymentForm 
                          customerInfo={customerInfo}
                          cart={cart}
                          total={total}
                          onSuccess={handlePaymentSuccess}
                          onBack={() => setStep('info')}
                          clientSecret={clientSecret}
                          orderId={orderId}
                          notifyOptIn={notifyOptIn}
                          user={user}
                          savePaymentMethod={savePaymentMethod}
                          setSavePaymentMethod={setSavePaymentMethod}
                        />
                      </Elements>
                    )}

                    {/* PayPal Payment */}
                    {paymentMethod === 'paypal' && PAYPAL_CLIENT_ID && (
                      <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6">
                        <PayPalScriptProvider options={{ 
                          clientId: PAYPAL_CLIENT_ID,
                          currency: "USD"
                        }}>
                          <PayPalButtons
                            style={{ 
                              layout: "vertical",
                              color: "gold",
                              shape: "rect",
                              label: "paypal"
                            }}
                            createOrder={async () => {
                              try {
                                setPaypalLoading(true);
                                const response = await axios.post(`${API_URL}/api/paypal/create-order`, {
                                  items: cart.map(item => ({
                                    product_id: item.product_id,
                                    quantity: item.quantity || 1
                                  })),
                                  customer_email: customerInfo.email,
                                  customer_name: customerInfo.name
                                });
                                setPaypalOrderId(response.data.paypal_order_id);
                                setOrderId(response.data.order_id);
                                return response.data.paypal_order_id;
                              } catch (error) {
                                toast.error('Failed to create PayPal order');
                                throw error;
                              } finally {
                                setPaypalLoading(false);
                              }
                            }}
                            onApprove={async (data) => {
                              try {
                                setPaypalLoading(true);
                                const response = await axios.post(`${API_URL}/api/paypal/capture-order/${data.orderID}`);
                                if (response.data.status === 'completed') {
                                  toast.success('Payment successful!');
                                  clearStoredCart();
                                  window.dispatchEvent(new Event('cartUpdated'));
                                  navigate(`/checkout/success?order_id=${response.data.order_id}&method=paypal`);
                                }
                              } catch (error) {
                                toast.error('Payment capture failed');
                              } finally {
                                setPaypalLoading(false);
                              }
                            }}
                            onError={(err) => {
                              console.error('PayPal error:', err);
                              toast.error('PayPal payment failed');
                            }}
                            onCancel={() => {
                              toast.info('Payment cancelled');
                            }}
                          />
                        </PayPalScriptProvider>
                        
                        {paypalLoading && (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-6 h-6 animate-spin text-[#0070ba]" />
                            <span className="ml-2 text-neutral-400">Processing...</span>
                          </div>
                        )}
                        
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => setStep('info')}
                          className="w-full mt-4"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-neutral-900/50 border border-yellow-500/30 rounded-2xl p-8 text-center">
                    <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
                    <h3 className="text-lg font-bold mb-2">Accept Terms to Continue</h3>
                    <p className="text-neutral-400">
                      Please read and accept the terms above to proceed with payment.
                    </p>
                  </div>
                )}
              </div>
            )}

            {step === 'payment' && (!stripeLoaded || !clientSecret) && (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
                <span className="ml-3 text-neutral-400">Loading payment form...</span>
              </div>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-neutral-900/50 border border-white/10 rounded-2xl p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-6">Order Summary</h2>

              {/* Items Count */}
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-neutral-400">Items ({cart.reduce((sum, item) => sum + (item.quantity || 1), 0)})</span>
                <span>${subtotal.toFixed(2)}</span>
              </div>

              {/* Platform Fee - with transparency info */}
              <div className="flex justify-between py-3 border-b border-white/5">
                <span className="text-neutral-400 flex items-center gap-1">
                  Platform Fee
                  <button
                    onClick={() => setShowFeeInfo(!showFeeInfo)}
                    className="text-[#00F0FF] hover:text-[#00F0FF]/80"
                  >
                    <Info className="w-3 h-3" />
                  </button>
                </span>
                <span className="text-[#00FF94]">FREE</span>
              </div>

              {/* Fee Transparency Info */}
              {showFeeInfo && (
                <div className="bg-neutral-800/50 rounded-lg p-3 my-2 text-xs">
                  <p className="text-neutral-400 mb-2">
                    <span className="text-white font-medium">Buyers pay no fees!</span>
                  </p>
                  <p className="text-neutral-500">
                    Sellers cover the fees:
                  </p>
                  <ul className="text-neutral-500 mt-1 space-y-0.5">
                    <li>• Platform fee: 5% of sale</li>
                    <li>• Stripe processing: ~2.9% + $0.30</li>
                  </ul>
                </div>
              )}

              {/* Coupon Code Input */}
              <div className="py-4 border-b border-white/5">
                <label className="text-sm text-neutral-400 mb-2 block">Coupon Code</label>
                {!couponApplied ? (
                  <div className="flex gap-2">
                    <Input
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder="Enter code"
                      className="bg-neutral-800 border-white/10 uppercase"
                    />
                    <Button
                      onClick={handleApplyCoupon}
                      disabled={couponLoading || !couponCode.trim()}
                      variant="outline"
                      className="border-[#00F0FF] text-[#00F0FF] hover:bg-[#00F0FF]/10 px-4"
                    >
                      {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between bg-[#00F0FF]/10 rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#00F0FF]" />
                      <span className="font-mono font-bold text-[#00F0FF]">{couponApplied.coupon_id ? couponCode : couponCode}</span>
                    </div>
                    <button
                      onClick={handleRemoveCoupon}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {/* Discount if coupon applied */}
              {couponApplied && (
                <div className="flex justify-between py-3 border-b border-white/5 text-[#00F0FF]">
                  <span className="flex items-center gap-2">
                    <Percent className="w-4 h-4" />
                    Discount
                  </span>
                  <span>-${discount.toFixed(2)}</span>
                </div>
              )}

              {/* Total */}
              <div className="flex justify-between py-4">
                <span className="text-xl font-bold">Total</span>
                <span className="text-2xl font-black text-[#00F0FF]">${total.toFixed(2)}</span>
              </div>

              {/* Upsell Offers */}
              {upsells.length > 0 && step === 'cart' && (
                <div className="mt-4 border-t border-white/5 pt-4">
                  <h3 className="text-sm font-bold text-[#00F0FF] flex items-center gap-2 mb-3">
                    <Gift className="w-4 h-4" />
                    {upsells.some(u => u.is_auto_suggested) ? 'Frequently Bought Together' : 'Special Offers'}
                  </h3>
                  <div className="space-y-3">
                    {upsells.map((upsell) => (
                      <div
                        key={upsell.upsell_id}
                        className={`border rounded-lg p-3 ${
                          upsell.is_auto_suggested 
                            ? 'bg-gradient-to-r from-[#00F0FF]/10 to-white/10 border-[#00F0FF]/30' 
                            : 'bg-gradient-to-r from-[#00F0FF]/10 to-[#00F0FF]/10 border-[#00F0FF]/30'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {upsell.upsell_product.images?.[0] && (
                            <ResilientImg
                              src={getImageUrl(upsell.upsell_product.images[0])}
                              alt=""
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className={`text-xs font-bold ${upsell.is_auto_suggested ? 'text-[#00F0FF]' : 'text-[#00F0FF]'}`}>
                              {upsell.headline}
                            </p>
                            <p className="text-sm font-medium truncate">{upsell.upsell_product.name}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-[#00F0FF] font-bold">${upsell.upsell_product.discounted_price.toFixed(2)}</span>
                              {upsell.discount_percent > 0 && (
                                <>
                                  <span className="text-xs text-neutral-500 line-through">${upsell.upsell_product.original_price.toFixed(2)}</span>
                                  <span className={`text-xs font-bold ${upsell.is_auto_suggested ? 'text-[#00F0FF]' : 'text-[#00F0FF]'}`}>
                                    -{upsell.discount_percent}%
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <Button
                          onClick={() => addUpsellToCart(upsell)}
                          size="sm"
                          className={`w-full mt-2 text-xs font-bold ${
                            upsell.is_auto_suggested 
                              ? 'bg-[#00F0FF] hover:bg-[#00C2CC] text-black' 
                              : 'bg-[#00F0FF] hover:bg-[#00C2CC] text-black'
                          }`}
                        >
                          <Sparkles className="w-3 h-3 mr-1" />
                          Add to Order
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Button */}
              {step === 'cart' && (
                <Button
                  onClick={handleProceedToInfo}
                  className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full py-6 text-lg font-bold mt-4"
                >
                  Proceed to Checkout
                </Button>
              )}

              {step === 'info' && paymentMethod !== 'test' && (
                <Button
                  onClick={handleStripeCheckoutWithTax}
                  disabled={!acceptedTerms || checkoutLoading || !customerInfo.email || !customerInfo.name}
                  className="w-full bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full py-6 text-lg font-bold mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {checkoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      <Lock className="w-5 h-5 mr-2" />
                      Continue to Secure Checkout
                    </>
                  )}
                </Button>
              )}

              {step === 'info' && paymentMethod === 'test' && (
                <Button
                  onClick={handleTestCheckout}
                  disabled={!acceptedTerms || testCheckoutLoading || !customerInfo.email || !customerInfo.name}
                  className="w-full bg-gradient-to-r from-[#00F0FF] to-[#00FF94] hover:opacity-90 text-black rounded-full py-6 text-lg font-bold mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {testCheckoutLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing Test Order...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Complete Test Purchase (No Charge)
                    </>
                  )}
                </Button>
              )}

              {step === 'info' && !acceptedTerms && (
                <p className="text-center text-xs text-yellow-400 mt-2">
                  Please accept the terms and conditions to continue
                </p>
              )}

              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 mt-6 text-neutral-500 text-sm">
                <Lock className="w-4 h-4" />
                <span>Secured by Stripe</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
